import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Lock } from "lucide-react";

/**
 * What a suspended installer sees when they sign in (INST-022).
 *
 * The real enforcement is in the database: a suspended installer is refused by
 * policy even on a session that is already open. This notice exists so that
 * refusal is explained rather than appearing as a page full of errors.
 *
 * It CANNOT read installer_profiles directly. The suspension rule covers that
 * table too, so the row that explains the refusal is itself refused. Prepared
 * patch 08 therefore provides one narrow authenticated function that returns
 * the CALLER'S own access state and nothing else, and that is what is read
 * here.
 *
 * It is deliberately non-blocking. The person may hold other roles, and their
 * route back to those, and to the office, stays available.
 */

export const OWN_ACCESS_RPC = "installer_my_portal_access";

export interface OwnAccess {
  suspended: boolean;
  reason: string | null;
}

/**
 * Reads the function's answer without ever inventing a suspension. Anything
 * unreadable, missing or not switched on means "nothing to say".
 */
export function interpretOwnAccess(payload: unknown): OwnAccess {
  if (!payload || typeof payload !== "object") return { suspended: false, reason: null };
  const row = payload as Record<string, unknown>;
  if (row.installer !== true) return { suspended: false, reason: null };
  if (row.portal_access !== "office_suspended") return { suspended: false, reason: null };
  return { suspended: true, reason: typeof row.reason === "string" ? row.reason : null };
}

export default function InstallerSuspendedNotice() {
  const [access, setAccess] = useState<OwnAccess>({ suspended: false, reason: null });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const check = async () => {
      try {
        const { data: session } = await supabase.auth.getUser();
        if (!session?.user?.id) {
          if (mounted.current) setAccess({ suspended: false, reason: null });
          return;
        }
        const { data, error } = await supabase.rpc(OWN_ACCESS_RPC as never);
        if (!mounted.current) return;
        // Before prepared patch 08 is applied the function does not exist and
        // this errors. Nothing is shown, which is correct: nobody is suspended
        // on a site where suspension is not switched on.
        if (error) return;
        setAccess(interpretOwnAccess(data));
      } catch {
        /* An unreadable check never invents a suspension. */
      }
    };

    void check();

    // Signing in as somebody else, or a token refresh after the office has
    // acted, must re-ask. An open session is exactly the case this covers.
    const listen = supabase.auth?.onAuthStateChange;
    const sub =
      typeof listen === "function"
        ? listen.call(supabase.auth, () => {
            void check();
          })?.data
        : undefined;

    return () => {
      mounted.current = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  if (!access.suspended) return null;

  return (
    <Alert variant="destructive" className="mb-6">
      <Lock className="h-4 w-4" />
      <AlertTitle>Your installer access is paused</AlertTitle>
      <AlertDescription className="space-y-1">
        <p>
          The EvoHome Improvements office has paused your access to installer work. Your account, your jobs, your quotes,
          your invoices and your uploaded files are all still here and nothing has been deleted.
        </p>
        {access.reason && <p className="text-xs opacity-90">Reason recorded: {access.reason}</p>}
        <p>
          Please contact the office on{" "}
          <a className="underline" href="mailto:info@evohomeimprovements.co.uk">
            info@evohomeimprovements.co.uk
          </a>{" "}
          to discuss it. Access is restored by the office.
        </p>
      </AlertDescription>
    </Alert>
  );
}
